import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { CommonIcons } from 'common-src/assets/Icons';
import { Button, RequestHandlerScreen, Select } from 'common-src/components/base';
import { apiRequest, HttpMethods } from 'common-src/features/rest';
import { openDocument } from 'common-src/models/PatientDocument';
import {
  AssessmentType,
  CmsTierOptions,
  getPatientGuideCmsAlignments,
  getPatientGuideCmsAlignmentsRequest,
  updatePatientGuideCmsAlignmentTier,
} from 'common-src/models/PatientGuideCmsAlignment';
import {
  getLastPatientDocumentByModuleType,
  getPatientInteractionsApiRequest,
  moduleTypes,
} from 'common-src/models/PatientInteraction';
import { Colors, ColorsNew } from 'common-src/styles';

import { ModalType } from 'src/components/base/ModalGroup/constants';
import { ActionsRow, BaseTable, TablePagination, TextItem } from 'src/components/table';
import { ResultsCount } from 'src/components/table/TablePagination/constants';
import { openModal } from 'src/features/modals';
import useAlert from 'src/hooks/useAlert';
import useCustomSelector from 'src/hooks/useCustomSelector';
import useInterval from 'src/hooks/useInterval';
import useRequestLoading from 'src/hooks/useRequestLoading';

import styles from './AlignmentDownload.module.scss';
import { TableHeaders } from './constants';

const AlignmentDownload = ({ patientId }) => {
  const dispatch = useDispatch();

  const [paginationData, setPaginationData] = useState({ from: 0, to: undefined });
  const [updatingTiers, setUpdatingTiers] = useState({});

  const alignments = useCustomSelector((state) => getPatientGuideCmsAlignments(state, patientId));
  const firstCallAssessment = useCustomSelector((state) =>
    getLastPatientDocumentByModuleType(state, patientId, moduleTypes.GUIDEFirstCall.id),
  );

  const { showAlert, AlertType } = useAlert();
  const { isRequesting } = useRequestLoading([
    getPatientGuideCmsAlignmentsRequest({ patientId }),
    getPatientInteractionsApiRequest({
      patientId,
      isDraft: false,
      module_types: [moduleTypes.GUIDEFirstCall.id],
    }),
  ]);

  useInterval(() => {
    dispatch(getPatientGuideCmsAlignmentsRequest({ patientId, sort_order: 'desc', limit: 5 }, {}));
  });

  const getData = () =>
    alignments.map((alignment) => ({
      assessmentType: (
        <TextItem
          isActive
          text={alignment.assessmentType}
          value={alignment.assessmentType}
          isUnderlined
          onClick={() => dispatch(openModal(ModalType.ALIGNMENT_DETAILS, { alignment, patientId }))}
        />
      ),
      tier: (
        <Select
          options={CmsTierOptions}
          classNames={[styles.select]}
          value={CmsTierOptions.find((option) => option.value === alignment.tier)}
          placeholder="-"
          isLoading={updatingTiers[alignment.id]}
          onChange={(option) => {
            setUpdatingTiers((prev) => ({ ...prev, [alignment.id]: true }));
            dispatch(
              updatePatientGuideCmsAlignmentTier(
                { id: alignment.id, tier: option.value },
                {
                  successBlock: () => {
                    showAlert(AlertType.Success, '', 'You have successfully updated CMS tier.');
                    setUpdatingTiers((prev) => ({ ...prev, [alignment.id]: false }));
                  },
                  errorBlock: (err) => {
                    showAlert(AlertType.Error, '', err);
                    setUpdatingTiers((prev) => ({ ...prev, [alignment.id]: false }));
                  },
                },
              ),
            );
          }}
          withError={false}
          size="small"
        />
      ),
      ptReferralSource: <TextItem isActive text={alignment.ptReferralSource ?? '-'} />,
      initialPatient: (
        <TextItem
          isActive
          text={
            typeof alignment.initialPatient === 'boolean'
              ? alignment.initialPatient
                ? 'Yes'
                : 'No'
              : '-'
          }
        />
      ),
      npi: <TextItem isActive text={alignment.npi ?? '-'} />,
      assessmentDate: (
        <TextItem
          isActive
          text={
            alignment.assessmentDate ? moment(alignment.assessmentDate).format('MM/DD/YYYY') : '-'
          }
          value={alignment.assessmentDate}
        />
      ),
      actions: (
        <ActionsRow
          options={[
            {
              id: 'download-button',
              text: 'Download',
              backgroundColor: '#555671B2',
              color: 'white',
              onClick: () => {
                apiRequest({
                  endpoint: `patientDocuments/${alignment.documentId}`,
                  method: HttpMethods.Get,
                })
                  .then((res) => {
                    if (!res.ok) {
                      throw new Error('Failed to download document');
                    }
                    return res.json();
                  })
                  .then((data) => {
                    openDocument(alignment.documentId, data.PatientDocument.fileName, (error) => {
                      showAlert(AlertType.Error, '', error.message);
                    });
                  })
                  .catch((error) => {
                    showAlert(AlertType.Error, '', error.message);
                  });
              },
            },
          ]}
        />
      ),
    }));

  if (isRequesting) {
    return <RequestHandlerScreen isRequesting requestingText="Pulling alignments..." />;
  }

  return (
    <>
      <div className={styles.header}>
        <Button
          id="new-alignment-button"
          text="Add Alignment"
          onClick={() =>
            dispatch(
              openModal(ModalType.ADD_ALIGNMENT, {
                patientId,
                assessmentType: firstCallAssessment ? AssessmentType.InitialAssessment : null,
                assessmentDate: firstCallAssessment
                  ? moment(firstCallAssessment.serviceAt).format('YYYY-MM-DD')
                  : null,
              }),
            )
          }
          backgroundColor={Colors.white}
          textColor={ColorsNew.darkGreen}
          iconSrc={CommonIcons.plusIcon}
          classNames={[styles.button]}
        />
      </div>
      <BaseTable
        headers={TableHeaders}
        data={getData()}
        name="sdoh"
        classNames={[
          'medical-table-new',
          alignments?.length <= ResultsCount[25] ? 'no-pagination' : '',
        ]}
        emptyText="No alignments"
        emptyClassNames={['medical-table-empty']}
        initialSortColumn={{
          column: 'assessmentDate',
          label: 'Assessment date',
          width: '10%',
          sortBy: 'value',
        }}
        paginationData={paginationData}
      />
      <TablePagination onChangePageHandler={setPaginationData} results={alignments} />
    </>
  );
};

AlignmentDownload.propTypes = {
  patientId: PropTypes.number,
};

export default AlignmentDownload;
